import React, { useMemo } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import BarChart from "components/charts/BarChart";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

/* =========================
   Utils
========================= */

function formatXAxisLabel(val, interval) {
  if (!val) return "";
  if (interval === "weekly") {
    const [y, w] = val.split("-W");
    const start = dayjs()
      .year(Number(y))
      .isoWeek(Number(w))
      .startOf("week")
      .add(1, "day");
    const end = start.add(6, "day");
    return `${start.format("MMM D")}–${end.format("D")}`;
  }
  return val;
}

/* =========================
   Component
========================= */

export default function SalesGraphCore({
  salesData,
  isLoading,
  interval = "weekly",
  metric = "revenue", // revenue | profit
  height = 280,
}) {
  /* =========================
     ✅ Hooks (항상 동일 순서)
  ========================= */

  const fg = salesData?.forecast_graph ?? {};

  const categories = useMemo(
    () => fg.forecast?.map((d) => d.label) ?? [],
    [fg]
  );

  const forecastVals = useMemo(
    () =>
      categories.map(
        (l) => fg.forecast?.find((x) => x.label === l)?.[metric] ?? null
      ),
    [categories, fg, metric]
  );

  const pendingDeltaVals = useMemo(
    () =>
      categories.map((l) => {
        const f = fg.forecast?.find((x) => x.label === l)?.[metric];
        const p =
          fg.with_pending_actions?.find((x) => x.label === l)?.[metric];

        if (!Number.isFinite(f) || !Number.isFinite(p)) return null;
        return Math.max(0, p - f); // ✅ 핵심
      }),
    [categories, fg, metric]
  );

  const baselineVals = useMemo(
    () =>
      categories.map(
        (l) => fg.baseline?.find((x) => x.label === l)?.[metric] ?? null
      ),
    [categories, fg, metric]
  );

  const demandVals = useMemo(
    () =>
      categories.map(
        (l) =>
          fg.forecast?.find((x) => x.label === l)?.demand_index ?? null
      ),
    [categories, fg]
  );

  const yBounds = useMemo(() => {
    const all = [...forecastVals, ...baselineVals].filter((v) =>
      Number.isFinite(v)
    );
    if (!all.length) return { min: 0, max: 100 };
    const min = Math.min(...all);
    const max = Math.max(...all);
    const pad = (max - min) * 0.25 || 10;
    return {
  min: Math.floor(min - 10),   // ❗ 무조건 최솟값 - 10
  max: Math.ceil(max + 5),
};
  }, [forecastVals, baselineVals]);

  const opacityByIndex = useMemo(() => {
    return categories.map((_, i) =>
      Math.max(0.35, 1 - i * 0.15)
    );
  }, [categories]);

  /* =========================
     ⛔ Loading / Empty
  ========================= */

  if (isLoading) return <div style={{ height }} />;
  if (!categories.length) return <div style={{ height }} />;

  /* =========================
     Series
  ========================= */

  const series = [
    {
      name: "Forecast",
      type: "bar",
      data: forecastVals,
    },
    {
      name: "Action Impact",
      type: "bar",
      data: pendingDeltaVals,
    },
    {
      name: "Baseline",
      type: "line",
      data: baselineVals,
    },
    {
      name: "Demand Index",
      type: "line",
      data: demandVals,
      yAxisIndex: 1,
    },
  ];

  /* =========================
     Options
  ========================= */

  const options = {
    chart: {
      type: "bar",
      stacked: false,
      background: "transparent",
      toolbar: { show: false },
      foreColor: "#B6B6B6",
    },

    plotOptions: {
      bar: {
        columnWidth: "56%",
        borderRadius: 15,
        borderRadiusApplication: "end",
      },
    },

    dataLabels: {
      enabled: false, // ❌ 숫자 제거
    },

    xaxis: {
      categories,
      labels: {
        formatter: (v) => formatXAxisLabel(v, interval),
        style: { colors: "#9CA3AF", fontSize: "12px" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },

    yaxis: [
      {
        min: yBounds.min,
        max: yBounds.max,
        labels: {
          formatter: (v) =>
            metric === "profit"
              ? `$${Math.round(v)}`
              : `$${Math.round(v)}`,
        },
      },
      {
        opposite: true,
        min: 80,
        max: 120,
        labels: {
          formatter: (v) => Math.round(v),
        },
      },
    ],

    colors: [
      ({ dataPointIndex }) =>
        `rgba(250,204,21,${opacityByIndex[dataPointIndex]})`, // Forecast
      ({ dataPointIndex }) =>
        `rgba(34,197,94,${opacityByIndex[dataPointIndex]})`, // Action delta
      "rgba(255,255,255,0.55)", // Baseline
      "rgba(96,165,250,0.9)", // Demand
    ],

    stroke: {
      width: [0, 0, 2, 2],
      dashArray: [0, 0, 6, 0],
      curve: "smooth",
    },

    grid: {
      borderColor: "rgba(255,255,255,0.08)",
      strokeDashArray: 4,
    },

    tooltip: {
      theme: "dark",
      shared: true,
      intersect: false,
    },

    legend: {
      labels: { colors: "#D1D5DB" },
      markers: { radius: 6 },
    },
  };

  return (
    <div style={{ height, width: "100%" }}>
      <BarChart chartData={series} chartOptions={options} />
    </div>
  );
}